import { Tooltip as MuiTooltip, useMediaQuery } from "@mui/material";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useLocation } from "react-router-dom";

// Register necessary components
ChartJS.register(ArcElement, Tooltip, Legend);

const DoughnutChart = ({
  data = [],
  borderwidth = 0,
  colors = ["#1954A6", "#FFC21D"],
  centeredData = {},
  "aria-label": ariaLabel,
  showPercentage = false,
  // New responsive props
  chartHeight,
  containerPadding,
  legendColumns,
  showLegend = true,
  cutout,
  compactMode = false,
  onSliceClick,
}) => {
  const location = useLocation();

  // Responsive breakpoints
  const isXsScreen = useMediaQuery("(max-width: 480px)"); // Very small phones
  const isSmScreen = useMediaQuery("(max-width: 640px)"); // Small phones
  const isMdScreen = useMediaQuery("(max-width: 768px)"); // Tablets
  const isLgScreen = useMediaQuery("(max-width: 1024px)"); // Small laptops

  
  const total =
  centeredData?.count ||
  data.slice(1).reduce((sum, item) => sum + item[1], 0) || 0;

  // Generate an accessible description of the chart data
  const generateChartDescription = () => {
    if (!data || data.length <= 1) return "No data available";

    const dataPoints = data.slice(1).map((item) => {
      const percentage = total > 0 ? Math.round((item[1] / total) * 100) : 0;
      return `${item[0]}: ${item[1]} (${percentage}%)`;
    });

    return `${
      centeredData?.title || "Chart"
    } showing ${total} total items. Breakdown: ${dataPoints.join(", ")}`;
  };

  // Get responsive dimensions - Made smaller
  const getResponsiveDimensions = () => {
    if (chartHeight) {
      return { height: chartHeight };
    }

    if (compactMode) {
      return {
        height: isXsScreen ? 100 : isSmScreen ? 110 : isMdScreen ? 120 : 130,
      };
    }

    return {
      height: isXsScreen
        ? 140
        : isSmScreen
        ? 160
        : isMdScreen
        ? 180
        : isLgScreen
        ? 200
        : 220,
    };
  };

  // Get responsive padding - Reduced padding
  const getResponsivePadding = () => {
    if (containerPadding) return containerPadding;

    return isXsScreen
      ? "6px"
      : isSmScreen
      ? "8px"
      : isMdScreen
      ? "10px"
      : "12px";
  };

  // Get responsive legend layout
  const getLegendLayout = () => {
    if (legendColumns) return { columns: legendColumns, layout: "grid" };

    // For mobile, use single column list layout
    if (isSmScreen) {
      return { columns: 1, layout: "list" };
    }

    // For tablets and small screens, use 2 columns
    if (isMdScreen) {
      return { columns: 2, layout: "grid" };
    }

    // For larger screens, use responsive columns based on data length
    const dataLength = data.length - 1;
    if (dataLength <= 3) return { columns: dataLength, layout: "grid" };
    if (dataLength <= 6) return { columns: 3, layout: "grid" };
    return { columns: 4, layout: "grid" };
  };

  // Get responsive centered text styles - Smaller font sizes
  const getCenteredTextStyles = () => {
    return {
      title: {
        fontSize: isXsScreen
          ? "9px"
          : isSmScreen
          ? "10px"
          : isMdScreen
          ? "11px"
          : "12px",
        lineHeight: isXsScreen
          ? "11px"
          : isSmScreen
          ? "12px"
          : isMdScreen
          ? "13px"
          : "14px",
        marginBottom: isXsScreen ? "1px" : "2px",
        fontWeight: "500",
      },
      count: {
        fontSize: isXsScreen
          ? "14px"
          : isSmScreen
          ? "18px"
          : isMdScreen
          ? "22px"
          : isLgScreen
          ? "26px"
          : "30px",
        lineHeight: isXsScreen
          ? "16px"
          : isSmScreen
          ? "20px"
          : isMdScreen
          ? "24px"
          : isLgScreen
          ? "28px"
          : "32px",
        fontWeight: "bold",
      },
    };
  };

  // Get responsive legend item styles - Smaller sizes
  const getLegendItemStyles = () => {
    const legendLayout = getLegendLayout();

    return {
      fontSize: isXsScreen ? "10px" : isSmScreen ? "11px" : "12px",
      padding:
        legendLayout.layout === "list"
          ? isXsScreen
            ? "4px 6px"
            : "5px 8px"
          : isXsScreen
          ? "3px 4px"
          : isSmScreen
          ? "4px 6px"
          : "5px 8px",
      gap: isXsScreen ? "4px" : isSmScreen ? "5px" : "6px",
      dotSize: isXsScreen ? "8px" : isSmScreen ? "9px" : "10px",
      minHeight: isXsScreen ? "24px" : isSmScreen ? "28px" : "32px",
    };
  };

  // Format data for Chart.js
  const chartData = {
    labels: data.slice(1).map((item) => item[0]),
    datasets: [
      {
        data: data.slice(1).map((item) => item[1]),
        backgroundColor: colors,
        borderWidth: borderwidth,
        borderColor: "#FFF",
        customData: data
        // borderRadius: isSmScreen ? 1 : 2,
      },
    ],
  };

  // Responsive chart options
  const options = {
    cutout: cutout
      ? `${cutout}%`
      : isXsScreen
      ? "55%"
      : isSmScreen
      ? "58%"
      : "60%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        yAlign: "bottom",
        xAlign: "center",
        // Custom filter to only show tooltips when hovering over actual chart segments
        filter: function (tooltipItem, data) {
          // Only show tooltip if we're actually hovering over a data segment
          return (
            tooltipItem.datasetIndex !== undefined &&
            tooltipItem.dataIndex !== undefined
          );
        },
        callbacks: {
          title: function (items) {
            return items[0].label;
          },
          label: function (context) {
            const value = context.parsed;
            // const total = context.dataset.data.reduce((a, b) => a + b, 0);
            // const percentage = Math.round((value / total) * 100);
            return `${context.label}: ${value}`;
          },
        },
        titleFont: {
          size: isXsScreen ? 9 : isSmScreen ? 10 : 11,
        },
        bodyFont: {
          size: isXsScreen ? 8 : isSmScreen ? 9 : 10,
        },
        padding: isXsScreen ? 6 : 8,
        displayColors: true,
        boxWidth: isXsScreen ? 6 : 8,
        boxHeight: isXsScreen ? 6 : 8,
        boxPadding: 6,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: isXsScreen ? 500 : 750,
    },
    interaction: {
      intersect: true, // Changed from false to true - only trigger on actual intersections
      mode: "point", // Changed from "nearest" to "point" - only trigger on exact points
    },
    // Add hover configuration to be more precise
    hover: {
      mode: "point",
      intersect: true,
    },
    // Add onHover callback to handle hover events more precisely
    onHover: (event, activeElements, chart) => {
      // Change cursor only when hovering over actual chart segments
      event.native.target.style.cursor =
        activeElements.length > 0 ? "pointer" : "default";
    },
    onClick: (event, activeElements) => {
    if (!activeElements.length) return;

    const dataIndex = activeElements[0].index;

    onSliceClick?.(dataIndex);
  },
  };

  const chartDescription = generateChartDescription();
  const dimensions = getResponsiveDimensions();
  const padding = getResponsivePadding();
  const legendLayout = getLegendLayout();
  const centeredStyles = getCenteredTextStyles();
  const legendStyles = getLegendItemStyles();

  return (
    <div
      className={`${
        !isMdScreen ? "flex" : "flex flex-col items-center"
      } rounded-md h-[280px] w-full mt-5`}
      style={{ padding }}
      role="region"
      aria-label={ariaLabel || "Donut chart"}
    >
      {/* Chart Container */}
      <div
        className={isMdScreen ? "mx-auto w-full max-w-[240px]" : "w-[60%]"}
        style={{
          position: "relative",
          height: `${dimensions.height}px`,
          marginBottom: showLegend ? (isSmScreen ? "8px" : "10px") : "0",
        }}
        key={location?.pathname}
        aria-hidden="true"
      >
        <Doughnut data={chartData} options={options} />

        {/* Centered Label */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: "#1D3557",
            zIndex: 2,
            maxWidth: isXsScreen ? "70px" : isSmScreen ? "85px" : "100px",
            pointerEvents: "none",
          }}
        >
          {centeredData?.title && (
            <div style={centeredStyles.title}>{centeredData.title}</div>
          )}
          {centeredData?.count !== undefined && (
            <div style={centeredStyles.count}>{centeredData.count}</div>
          )}
        </div>
      </div>

      {/* Screen reader only description */}
      <div className="sr-only " aria-live="polite">
        {chartDescription}
      </div>

      {/* Legend */}
      {showLegend && chartData?.labels?.length > 0 && (
        <div
          className={`flex-col pt-2 items-center justify-evenly ${
            chartData?.labels?.length > 6 ? "overflow-auto" : ""
          } w-full`}
        >
          {legendLayout.layout === "list" ? (
            // List layout for mobile
            <div className="space-y-1">
              {chartData.labels.map((label, idx) => (
                <div
                  key={`${label}-${idx}`}
                  className="flex items-center justify-between p-1 hover:bg-gray-50 rounded transition-colors duration-200"
                  style={{
                    fontSize: legendStyles.fontSize,
                    minHeight: legendStyles.minHeight,
                  }}
                  role="listitem"
                >
                  <div
                    className="flex items-center flex-1 min-w-0"
                    style={{ gap: legendStyles.gap }}
                  >
                    <div
                      className="rounded-full flex-shrink-0"
                      style={{
                        width: legendStyles.dotSize,
                        height: legendStyles.dotSize,
                        backgroundColor: colors[idx % colors.length],
                      }}
                      aria-hidden="true"
                    />

                    <span className="flex-1 min-w-0 font-medium text-gray-700">
                      {label}
                    </span>
                  </div>

                  <span className="flex-shrink-0 font-bold text-gray-900 ml-2">
                    {chartData.datasets[0].data[idx]}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            // Grid layout for larger screens
            <div
              className={`w-full ${
                isMdScreen
                  ? "flex flex-wrap justify-center items-center mt-4"
                  : "h-full flex flex-col justify-center"
              }`}
              style={{
                marginTop: isSmScreen ? "10px" : "12px",
                gap: isSmScreen ? "6px" : isMdScreen ? "8px" : "10px",
              }}
              role="list"
              aria-label="Chart legend"
            >
              {chartData.labels.map((label, idx) => (
                <div
                  key={`${label}-${idx}`}
                  className="flex items-center p-1 hover:bg-gray-50 rounded transition-colors duration-200"
                  style={{
                    fontSize: legendStyles.fontSize,
                    minHeight: legendStyles.minHeight,
                  }}
                  role="listitem"
                >
                  <div
                    className="flex items-center"
                    style={{ gap: legendStyles.gap }}
                  >
                    <div
                      className="flex-shrink-0 mx-2"
                      style={{
                        width: legendStyles.dotSize,
                        height: legendStyles.dotSize,
                        backgroundColor: colors[idx % colors.length],
                      }}
                      aria-hidden="true"
                    />
                  </div>

                  <MuiTooltip
                    title={label}
                    placement="top"
                    arrow
                    enterDelay={500}
                  >
                    <span
                      className="text-gray-600 leading-tight cursor-auto"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                        fontSize: `${parseInt(legendStyles.fontSize) - 1}px`,
                      }}
                    >
                      {label}
                      {" - "}
                      {chartData.datasets[0].data[idx]}
                      {showPercentage && ` (${Math.round((chartData.datasets[0].data[idx] / total  ) * 100)}%)`}
                    </span>
                  </MuiTooltip>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DoughnutChart;
